import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { restoreAdminCache } from 'modules/state-mgmt/cache';
import { getAdminContext } from 'utils/adminContext';

/**
 * Hook to restore the admin's saved project context into Redux.
 *
 * When a worker logs in after the admin and navigates to a different project,
 * the Redux cache gets overwritten. This hook detects a mismatch between
 * the current `cache.project` and the saved admin context, then restores
 * the saved snapshot so the admin's project data is preserved.
 *
 * Returns:
 * - `restoreIfNeeded()`: Call on mount. Returns `true` if restoration occurred,
 *    or `false` if no restoration was needed.
 */
const useRestoreAdminContext = () => {
  const dispatch = useAppDispatch();
  const { cacheProjectId, isWorker } = useAppSelector(state => ({
    cacheProjectId: state.cache.project?.id,
    isWorker: state.user.isWorker,
  }));

  const restoreIfNeeded = useCallback(async (): Promise<boolean> => {
    // Only restore for non-worker (admin) users
    if (isWorker) return false;

    const adminContext = await getAdminContext();
    if (!adminContext) return false;

    const savedProjectId = adminContext.cache?.project?.id;
    if (!savedProjectId || !cacheProjectId) return false;

    // If the current cache already matches the saved admin project, no restore needed
    if (cacheProjectId === savedProjectId) return false;

    // Restore the full cache state
    dispatch(restoreAdminCache(adminContext.cache));

    return true;
  }, [dispatch, cacheProjectId, isWorker]);

  return { restoreIfNeeded };
};

export default useRestoreAdminContext;
